// Раздел "Статистика" (docs/PLAN.md, запрос пользователя 2026-08-21) — подписки/тарифы за период:
// новые клиенты, приобретённые устройства, популярность тарифов, обращения в поддержку (/support,
// лог добавлен 2026-08-21, тем же запросом).
import { type FormEvent, useEffect, useState } from "react";

import {
  type AdminStats,
  ApiError,
  fetchAdminStats,
  type StatsGranularity,
} from "../api/client";
import { BarChart } from "../components/BarChart";
import { RankedBarChart } from "../components/RankedBarChart";

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function startOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfYear(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1);
}

// Пресеты — строкой в один клик, дата-пикер календарём никто не любит выбирать "последние 30
// дней" (dataviz skill, interaction.md: "presets (today, last 7/30/90 days) before a custom range").
const PRESETS: { label: string; granularity: StatsGranularity; from: () => Date }[] = [
  { label: "7 дней", granularity: "day", from: () => daysAgo(6) },
  { label: "30 дней", granularity: "day", from: () => daysAgo(29) },
  { label: "Этот месяц", granularity: "day", from: startOfMonth },
  { label: "Этот год", granularity: "month", from: startOfYear },
];

export function StatsScreen() {
  const [granularity, setGranularity] = useState<StatsGranularity>("day");
  const [dateFrom, setDateFrom] = useState(toDateInput(daysAgo(29)));
  const [dateTo, setDateTo] = useState(toDateInput(new Date()));
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function load(g: StatsGranularity, from: string, to: string) {
    setLoading(true);
    setError(null);
    fetchAdminStats(g, from, to)
      .then(setStats)
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить статистику");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(granularity, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyPreset(preset: (typeof PRESETS)[number]) {
    const from = toDateInput(preset.from());
    const to = toDateInput(new Date());
    setGranularity(preset.granularity);
    setDateFrom(from);
    setDateTo(to);
    load(preset.granularity, from, to);
  }

  function onSubmitCustomRange(event: FormEvent) {
    event.preventDefault();
    load(granularity, dateFrom, dateTo);
  }

  return (
    <div className="screen">
      <h2>Статистика</h2>

      {/* Фильтры одной строкой над содержимым, они же управляют всеми графиками разом (dataviz
          skill, interaction.md: "one row, above the charts", "filters scope everything below"). */}
      <form onSubmit={onSubmitCustomRange} className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="secondary"
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>С</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>По</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Группировка</label>
            <select value={granularity} onChange={(e) => setGranularity(e.target.value as StatsGranularity)}>
              <option value="day">По дням</option>
              <option value="month">По месяцам</option>
              <option value="year">По годам</option>
            </select>
          </div>
          <button type="submit" className="primary" disabled={loading}>
            {loading ? "Считаем…" : "Показать"}
          </button>
        </div>
      </form>

      {error && <div className="error-text">{error}</div>}

      {stats && (
        <>
          <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
            <div className="card" style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Новых клиентов</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{stats.total_new_clients}</div>
            </div>
            <div className="card" style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Устройств приобретено</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{stats.total_devices_purchased}</div>
            </div>
            <div className="card" style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Обращений в поддержку</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{stats.total_support_requests}</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0 }}>Новые клиенты</h3>
            <BarChart data={stats.new_clients.map((b) => ({ label: b.bucket, value: b.count }))} />
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0 }}>Приобретено устройств</h3>
            <BarChart data={stats.devices_purchased.map((b) => ({ label: b.bucket, value: b.count }))} />
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0 }}>Популярность тарифов</h3>
            <RankedBarChart
              data={stats.plan_popularity.map((p) => ({ label: p.plan_title, value: p.count }))}
            />
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>Обращения в поддержку</h3>
            <BarChart data={stats.support_requests.map((b) => ({ label: b.bucket, value: b.count }))} />
          </div>
        </>
      )}
    </div>
  );
}
